import React, { useState } from 'react';
import { tintFor } from '../../utils/tints';
import { coverShape, rememberRatio } from './clubMasonry';
import { RING } from '../wall/wallTokens';
import './clubsMotion.css';

/**
 * A club in the grid (clubs redesign handoff §2 and §3).
 *
 * No border at rest — white on the canvas — and on hover the Portal ring becomes the border: a
 * transparent 2px border with white in the padding box and the ring gradient in the border box,
 * so the colours only ever show where the border is. The card lifts, the cover zooms, a shine
 * crosses it once and an arrow slides in beside the name.
 *
 * Hover is for pointers only: on a touch screen the "hover" would latch on tap and stay there.
 */

const CYAN = '#00FFFF';
const PLAIN = 'linear-gradient(#fff,#fff) padding-box, linear-gradient(#fff,#fff) border-box';
const RINGED = `linear-gradient(#fff,#fff) padding-box, ${RING} border-box`;

const overlayPill = {
  position: 'absolute',
  display: 'flex',
  alignItems: 'center',
  gap: 5,
  padding: '4px 9px',
  borderRadius: 999,
  fontSize: 9,
  fontWeight: 700,
  letterSpacing: '0.12em',
  whiteSpace: 'nowrap',
};

function LockIcon() {
  return (
    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="square">
      <rect x="5" y="11" width="14" height="10" />
      <path d="M8 11V7a4 4 0 0 1 8 0v4" />
    </svg>
  );
}

/** OPEN / JOIN / REQUEST / REQUESTED / JOINED, and what each looks like. */
export function joinAction(club) {
  const status = club.viewerStatus || (club.userRole ? 'member' : 'none');
  if (status === 'member') return { key: 'member', label: 'OPEN' };
  if (status === 'joined') return { key: 'joined', label: 'JOINED ✓' };
  if (status === 'requested') return { key: 'requested', label: 'REQUESTED' };
  // A private club, or a public one whose owner switched approval on, is asked rather than joined.
  return club.requiresApproval || club.isPublic === false
    ? { key: 'request', label: 'REQUEST' }
    : { key: 'join', label: 'JOIN' };
}

export default function ClubCard({ club, ratio, onRatio, onOpen, onJoin, busy = false, hoverable = true }) {
  const [hot, setHot] = useState(false);
  const live = hot && hoverable;

  // A club that owns a picture shouldn't be shown as a letter on a tint, and most own one
  // somewhere other than the banner slot. `coverUrl` is already the banner or, failing that, the
  // club page's wallpaper (ClubService); the mark is the last resort before the initial tile —
  // and when the mark IS the cover it isn't drawn again as the corner circle, which is only ever
  // the mark sitting ON a photo of the club.
  const cover = club.coverUrl || club.avatarUrl || null;
  const showMark = !!club.coverUrl && !!club.avatarUrl;
  const shape = coverShape(cover ? ratio : 0);
  const initial = club.name?.charAt(0)?.toUpperCase() || '?';
  const faces = (club.memberPreview || []).slice(0, 3);
  const more = Math.max(0, (club.memberCount ?? 0) - faces.length);
  const action = joinAction(club);
  const settled = action.key === 'joined' || action.key === 'requested';

  return (
    <article
      onClick={onOpen}
      onMouseEnter={() => setHot(true)}
      onMouseLeave={() => setHot(false)}
      data-testid="club-card"
      data-club-id={club.id}
      data-portal-to={`/clubs/${club.id}`}
      data-clb-motion
      style={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        cursor: 'pointer',
        color: '#000',
        border: '2px solid transparent',
        background: live ? RINGED : PLAIN,
        transform: live ? 'translateY(-4px)' : 'none',
        transition: 'transform .35s cubic-bezier(.2,.7,.2,1)',
        animation: 'clbRise 320ms cubic-bezier(0,0,.2,1) both',
      }}
    >
      <span style={{ position: 'relative', display: 'block', aspectRatio: shape.aspect, background: tintFor(club.id || club.name), overflow: 'hidden' }}>
        {cover ? (
          <img
            src={cover}
            alt=""
            referrerPolicy="no-referrer"
            onLoad={(e) => {
              const img = e.target;
              if (!img.naturalWidth || !img.naturalHeight) return;
              const measured = img.naturalWidth / img.naturalHeight;
              if (rememberRatio(cover, measured) && onRatio) onRatio(cover, measured);
            }}
            style={{
              display: 'block',
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              // The card photo's focal point (EDIT COVER on the club page) is what stays in frame.
              objectPosition: club.coverUrl ? `${club.coverFocalX ?? 50}% ${club.coverFocalY ?? 50}%` : 'center',
              transform: live ? 'scale(1.06)' : 'scale(1)',
              transition: 'transform .6s cubic-bezier(.2,.7,.2,1)',
            }}
          />
        ) : (
          <span
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 56,
              fontWeight: 800,
              transform: live ? 'scale(1.06)' : 'scale(1)',
              transition: 'transform .6s cubic-bezier(.2,.7,.2,1)',
            }}
          >
            {initial}
          </span>
        )}

        {live && (
          <span
            aria-hidden="true"
            data-clb-motion
            style={{ position: 'absolute', top: 0, bottom: 0, left: 0, width: '45%', background: 'linear-gradient(100deg, rgba(255,255,255,0), rgba(255,255,255,.45), rgba(255,255,255,0))', animation: 'clbShine .9s cubic-bezier(.2,.7,.2,1) both', pointerEvents: 'none' }}
          />
        )}

        {club.hasLivePortal && (
          <span style={{ ...overlayPill, left: 10, top: 10, background: '#FF00AA', color: '#fff' }} data-testid="club-card-live">
            <span data-clb-motion style={{ width: 6, height: 6, borderRadius: '50%', background: '#fff', animation: 'clbPulse 1.6s ease-in-out infinite' }} />
            PORTAL LIVE
          </span>
        )}
        {club.isPublic === false && (
          <span style={{ ...overlayPill, right: 10, top: 10, background: '#000', color: '#fff' }}>
            <LockIcon />
            PRIVATE
          </span>
        )}
        {showMark && (
          <span data-testid="club-card-mark" style={{ position: 'absolute', left: 10, bottom: 10, width: 44, height: 44, borderRadius: '50%', overflow: 'hidden', background: '#fff', border: '2px solid #fff', boxSizing: 'border-box' }}>
            <img src={club.avatarUrl} alt="" referrerPolicy="no-referrer" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
          </span>
        )}
      </span>

      <span style={{ padding: '14px 16px 16px', display: 'flex', flexDirection: 'column', gap: 12 }}>
        <span style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
          <span style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10 }}>
            <span className="line-clamp-2" style={{ fontSize: 15, lineHeight: 1.25, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
              {club.name}
            </span>
            <span
              aria-hidden="true"
              style={{ flexShrink: 0, fontSize: 16, lineHeight: 1.2, opacity: live ? 1 : 0, transform: live ? 'translateX(0)' : 'translateX(-8px)', transition: 'opacity .25s, transform .35s cubic-bezier(.2,.7,.2,1)' }}
            >
              →
            </span>
          </span>
          <span className="line-clamp-3" style={{ fontSize: 12, lineHeight: 1.5, color: '#4B5563' }}>
            {club.tagline || club.description || club.ritual || 'A room of your people.'}
          </span>
        </span>

        <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
            <span style={{ display: 'flex', paddingLeft: faces.length || more ? 8 : 0 }}>
              {faces.map((url) => (
                <img key={url} src={url} alt="" referrerPolicy="no-referrer" style={{ width: 28, height: 28, marginLeft: -8, border: '2px solid #fff', borderRadius: '50%', objectFit: 'cover', boxSizing: 'border-box', display: 'block', flexShrink: 0 }} />
              ))}
              {more > 0 && (
                <span style={{ height: 28, marginLeft: -8, padding: '0 8px', border: '2px solid #fff', borderRadius: 999, boxSizing: 'border-box', background: '#000', color: '#fff', display: 'flex', alignItems: 'center', fontSize: 9, fontWeight: 700 }}>
                  +{more}
                </span>
              )}
            </span>
            <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.12em', whiteSpace: 'nowrap' }}>
              {club.memberCount ?? 0} MEMBER{(club.memberCount ?? 0) === 1 ? '' : 'S'}
            </span>
          </span>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); if (action.key === 'member') onOpen(); else if (!settled) onJoin(action.key); }}
            disabled={busy || settled}
            style={{
              flexShrink: 0,
              height: 32,
              padding: '0 14px',
              border: 'none',
              borderRadius: 999,
              fontFamily: 'inherit',
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: '0.14em',
              cursor: settled ? 'default' : 'pointer',
              background: settled ? CYAN : action.key === 'member' ? (live ? '#000' : '#F2F2F2') : '#000',
              color: settled ? '#000' : action.key === 'member' ? (live ? '#fff' : '#000') : '#fff',
              transition: 'background-color .2s, color .2s',
            }}
          >
            {busy ? '…' : action.label}
          </button>
        </span>
      </span>
    </article>
  );
}
